import { useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ArrowUpDown, X } from "lucide-react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DIMENSION_SLUGS,
  SORT_ORDER,
  PAGE_SIZE_OPTIONS,
} from "@/constants/search";
import type { SearchFilterDimension, SearchFilterValue } from "@/types/search";
import "@/components/shared/FilterButton.css";

/** Props for AdvancedFiltersContent (no Collapsible wrapper) */
interface AdvancedFiltersContentProps {
  filters: SearchFilterDimension[];
  selectedAttributes: string[];
  onAttributesChange: (slugs: string[]) => void;
  selectedEntityTypes: string[];
  onEntityTypesChange: (slugs: string[]) => void;
  dateFilter: string;
  onDateFilterChange: (slug: string) => void;
  sortBy: string;
  onSortByChange: (slug: string) => void;
  sortOrder: "asc" | "desc";
  onSortOrderChange: (order: "asc" | "desc") => void;
  searchMode: string;
  onSearchModeChange: (mode: string) => void;
  limit: number;
  onLimitChange: (limit: number) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  isProjectScoped?: boolean;
  /** Total count of items (for "Showing X of Y" display) */
  total?: number;
  /** Number of currently visible items */
  visibleCount?: number;
  /** Whether data is currently being fetched (for loading indicator) */
  isFetching?: boolean;
}

/** Props for AdvancedFilters (with Collapsible wrapper) */
interface AdvancedFiltersProps extends AdvancedFiltersContentProps {
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Translates a translation key to a display label.
 * Falls back to extracting the last segment of the key and converting camelCase to Title Case.
 *
 * TODO: Replace with `t(tk)` from useTranslation() once search filter translation keys
 * are added to /public/locales/en/common.json. Pattern:
 *   const { t } = useTranslation();
 *   const label = t(tk, { defaultValue: labelFromTk(tk) });
 */
function labelFromTk(tk: string): string {
  const lastSegment = tk.split(".").pop() ?? tk;
  return lastSegment
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/^Is /, "")
    .trim();
}

/** Sorts filter values by their sortOrder property */
function sortValuesBySortOrder(
  values: SearchFilterValue[],
): SearchFilterValue[] {
  return [...values].sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Renders a multi-select filter dimension as a group of toggle buttons.
 */
function MultiSelectDimension({
  dimension,
  selectedSlugs,
  onSelectionChange,
}: {
  dimension: SearchFilterDimension;
  selectedSlugs: string[];
  onSelectionChange: (slugs: string[]) => void;
}) {
  const handleToggle = useCallback(
    (slug: string) => {
      const isSelected = selectedSlugs.includes(slug);
      if (isSelected) {
        // Don't allow deselecting if it's the last one
        if (selectedSlugs.length <= 1) return;
        onSelectionChange(selectedSlugs.filter((s) => s !== slug));
      } else {
        onSelectionChange([...selectedSlugs, slug]);
      }
    },
    [selectedSlugs, onSelectionChange],
  );

  const sortedValues = useMemo(
    () => sortValuesBySortOrder(dimension.values),
    [dimension.values],
  );

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm text-muted-foreground w-24 flex-shrink-0 text-left">
        {labelFromTk(dimension.tk)}
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        {sortedValues.map((value) => {
          const isSelected = selectedSlugs.includes(value.slug);
          return (
            <button
              key={value.slug}
              type="button"
              onClick={() => handleToggle(value.slug)}
              className={`filter-btn ${isSelected ? "filter-btn--active" : ""}`}
              style={{
                height: "32px",
                padding: "0 12px",
                fontSize: "13px",
                fontWeight: 500,
                borderRadius: "var(--radius-default)",
                border: isSelected
                  ? "1px solid transparent"
                  : "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.12)",
                backgroundColor: isSelected
                  ? "rgba(var(--color-primitive-pink-500-rgb),0.12)"
                  : "var(--color-panel)",
                color: isSelected
                  ? "var(--color-primitive-neutral-0)"
                  : "var(--color-primitive-neutral-200)",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor =
                    "rgba(var(--color-primitive-neutral-0-rgb),0.1)";
                  e.currentTarget.style.borderColor =
                    "rgba(var(--color-primitive-neutral-0-rgb),0.2)";
                  e.currentTarget.style.color =
                    "var(--color-primitive-neutral-0)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = "var(--color-panel)";
                  e.currentTarget.style.borderColor =
                    "rgba(var(--color-primitive-neutral-0-rgb),0.12)";
                  e.currentTarget.style.color =
                    "var(--color-primitive-neutral-200)";
                }
              }}
            >
              {labelFromTk(value.tk)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Renders a page size dropdown with consistent styling.
 */
function PageSizeDropdown({
  limit,
  onLimitChange,
}: {
  limit: number;
  onLimitChange: (limit: number) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm text-muted-foreground w-24 flex-shrink-0 text-left">
        Page Size
      </span>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`filter-btn ${isOpen ? "filter-btn--active" : ""}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
              height: "32px",
              padding: "0 12px",
              fontSize: "13px",
              fontWeight: 500,
              borderRadius: "var(--radius-default)",
              border: isOpen
                ? "1px solid transparent"
                : isHovered
                  ? "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.2)"
                  : "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.12)",
              backgroundColor: isOpen
                ? "rgba(var(--color-primitive-pink-500-rgb),0.12)"
                : isHovered
                  ? "rgba(var(--color-primitive-neutral-0-rgb),0.1)"
                  : "var(--color-panel)",
              color: isOpen
                ? "var(--color-primitive-neutral-0)"
                : isHovered
                  ? "var(--color-primitive-neutral-0)"
                  : "var(--color-primitive-neutral-200)",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {limit}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          style={{
            backgroundColor: "var(--color-panel)",
            border: "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.12)",
            borderRadius: "var(--radius-default)",
            boxShadow:
              "0 4px 6px rgba(var(--color-primitive-neutral-1000-rgb),0.3)",
            padding: "4px",
          }}
        >
          {PAGE_SIZE_OPTIONS.map((size) => {
            const isSelected = limit === size;
            return (
              <DropdownMenuItem
                key={size}
                onClick={() => onLimitChange(size)}
                className={`cursor-pointer filter-menu-item ${isSelected ? "filter-menu-item--selected" : ""}`}
                style={{
                  fontSize: "13px",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  margin: "2px 4px",
                  border: "1px solid transparent",
                  backgroundColor: isSelected
                    ? "rgba(var(--color-primitive-pink-500-rgb),0.12)"
                    : "transparent",
                  borderColor: "transparent",
                  color: isSelected
                    ? "var(--color-primitive-neutral-0)"
                    : undefined,
                  transition: "all 0.15s ease",
                }}
              >
                {size}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Renders a select filter dimension as a dropdown.
 */
function SelectDimension({
  dimension,
  selectedSlug,
  onSelectionChange,
}: {
  dimension: SearchFilterDimension;
  selectedSlug: string;
  onSelectionChange: (slug: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const selectedValue = dimension.values.find((v) => v.slug === selectedSlug);
  const selectedLabel = selectedValue
    ? labelFromTk(selectedValue.tk)
    : labelFromTk(dimension.tk);

  const sortedValues = useMemo(
    () => sortValuesBySortOrder(dimension.values),
    [dimension.values],
  );

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm text-muted-foreground w-24 flex-shrink-0 text-left">
        {labelFromTk(dimension.tk)}
      </span>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`filter-btn ${isOpen ? "filter-btn--active" : ""}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
              height: "32px",
              padding: "0 12px",
              fontSize: "13px",
              fontWeight: 500,
              borderRadius: "var(--radius-default)",
              border: isOpen
                ? "1px solid transparent"
                : isHovered
                  ? "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.2)"
                  : "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.12)",
              backgroundColor: isOpen
                ? "rgba(var(--color-primitive-pink-500-rgb),0.12)"
                : isHovered
                  ? "rgba(var(--color-primitive-neutral-0-rgb),0.1)"
                  : "var(--color-panel)",
              color: isOpen
                ? "var(--color-primitive-neutral-0)"
                : isHovered
                  ? "var(--color-primitive-neutral-0)"
                  : "var(--color-primitive-neutral-200)",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {selectedLabel}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          style={{
            backgroundColor: "var(--color-panel)",
            border: "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.12)",
            borderRadius: "var(--radius-default)",
            boxShadow:
              "0 4px 6px rgba(var(--color-primitive-neutral-1000-rgb),0.3)",
            padding: "4px",
          }}
        >
          {sortedValues.map((value) => {
            const isSelected = selectedSlug === value.slug;
            return (
              <DropdownMenuItem
                key={value.slug}
                onClick={() => onSelectionChange(value.slug)}
                className={`cursor-pointer filter-menu-item ${isSelected ? "filter-menu-item--selected" : ""}`}
                style={{
                  fontSize: "13px",
                  padding: "8px 12px",
                  borderRadius: "6px",
                  margin: "2px 4px",
                  border: "1px solid transparent",
                  backgroundColor: isSelected
                    ? "rgba(var(--color-primitive-pink-500-rgb),0.12)"
                    : "transparent",
                  borderColor: "transparent",
                  color: isSelected
                    ? "var(--color-primitive-neutral-0)"
                    : undefined,
                  transition: "all 0.15s ease",
                }}
              >
                {labelFromTk(value.tk)}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Advanced filters content (without Collapsible wrapper).
 * Use this when the parent component handles the Collapsible.
 */
export function AdvancedFiltersContent({
  filters,
  selectedAttributes,
  onAttributesChange,
  selectedEntityTypes,
  onEntityTypesChange,
  dateFilter,
  onDateFilterChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  searchMode,
  onSearchModeChange,
  limit,
  onLimitChange,
  onClearFilters,
  hasActiveFilters,
  isProjectScoped = false,
  total,
  visibleCount,
  isFetching = false,
}: AdvancedFiltersContentProps) {
  // Filter out "project" from entity types when project-scoped
  const processedFilters = useMemo(() => {
    if (!isProjectScoped) return filters;

    return filters.map((dimension) => {
      if (dimension.slug === DIMENSION_SLUGS.ENTITY_TYPE) {
        return {
          ...dimension,
          values: dimension.values.filter((v) => v.slug !== "project"),
        };
      }
      return dimension;
    });
  }, [filters, isProjectScoped]);

  const renderDimension = (dimension: SearchFilterDimension) => {
    if (dimension.inputType === "multi-select") {
      // Multi-select dimensions
      let selectedSlugs: string[] = [];
      let onSelectionChange: (slugs: string[]) => void = () => undefined;

      if (dimension.slug === DIMENSION_SLUGS.STATUS_ATTRIBUTE) {
        selectedSlugs = selectedAttributes;
        onSelectionChange = onAttributesChange;
      } else if (dimension.slug === DIMENSION_SLUGS.ENTITY_TYPE) {
        selectedSlugs = selectedEntityTypes;
        onSelectionChange = onEntityTypesChange;
      } else {
        return null;
      }

      return (
        <MultiSelectDimension
          key={dimension.slug}
          dimension={dimension}
          selectedSlugs={selectedSlugs}
          onSelectionChange={onSelectionChange}
        />
      );
    }

    // Select or date-range dimensions
    let selectedSlug = "";
    let onSelectionChange: (slug: string) => void = () => undefined;

    if (dimension.slug === DIMENSION_SLUGS.DATE_RANGE) {
      selectedSlug = dateFilter;
      onSelectionChange = onDateFilterChange;
    } else if (dimension.slug === DIMENSION_SLUGS.SORT) {
      selectedSlug = sortBy;
      onSelectionChange = onSortByChange;
    } else if (dimension.slug === DIMENSION_SLUGS.SEARCH_MODE) {
      selectedSlug = searchMode;
      onSelectionChange = onSearchModeChange;
    } else {
      return null;
    }

    return (
      <SelectDimension
        key={dimension.slug}
        dimension={dimension}
        selectedSlug={selectedSlug}
        onSelectionChange={onSelectionChange}
      />
    );
  };

  if (processedFilters.length === 0) return null;

  // Sort dimensions by sortOrder, but put "sort" dimension separate
  // so we can render the sort order toggle next to it
  const sortedFilters = [...processedFilters].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Render all dimensions except search-mode (rendered last) */}
      {sortedFilters
        .filter((d) => d.slug !== DIMENSION_SLUGS.SEARCH_MODE)
        .map((dimension) => {
          const rendered = renderDimension(dimension);
          if (!rendered) return null;

          // Add sort order toggle next to the sort dimension
          if (dimension.slug === DIMENSION_SLUGS.SORT) {
            return (
              <div key={dimension.slug} className="flex items-center gap-2">
                {rendered}
                <button
                  type="button"
                  onClick={() =>
                    onSortOrderChange(
                      sortOrder === SORT_ORDER.ASC
                        ? SORT_ORDER.DESC
                        : SORT_ORDER.ASC,
                    )
                  }
                  aria-label={
                    sortOrder === SORT_ORDER.ASC
                      ? "Sort ascending, click to sort descending"
                      : "Sort descending, click to sort ascending"
                  }
                  style={{
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "var(--radius-default)",
                    border:
                      "1px solid rgba(var(--color-primitive-neutral-0-rgb),0.12)",
                    backgroundColor: "var(--color-panel)",
                    color: "var(--color-primitive-neutral-200)",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(var(--color-primitive-neutral-0-rgb),0.1)";
                    e.currentTarget.style.borderColor =
                      "rgba(var(--color-primitive-neutral-0-rgb),0.2)";
                    e.currentTarget.style.color =
                      "var(--color-primitive-neutral-0)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--color-panel)";
                    e.currentTarget.style.borderColor =
                      "rgba(var(--color-primitive-neutral-0-rgb),0.12)";
                    e.currentTarget.style.color =
                      "var(--color-primitive-neutral-200)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(var(--color-primitive-pink-500-rgb),0.15)";
                    e.currentTarget.style.borderColor =
                      "rgba(var(--color-primitive-pink-500-rgb),0.4)";
                    e.currentTarget.style.color =
                      "var(--color-primitive-pink-500)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(var(--color-primitive-neutral-0-rgb),0.1)";
                    e.currentTarget.style.borderColor =
                      "rgba(var(--color-primitive-neutral-0-rgb),0.2)";
                    e.currentTarget.style.color =
                      "var(--color-primitive-neutral-0)";
                  }}
                >
                  <ArrowUpDown style={{ width: "16px", height: "16px" }} />
                </button>
                <span className="text-xs text-muted-foreground">
                  {sortOrder === SORT_ORDER.ASC ? "Asc" : "Desc"}
                </span>
              </div>
            );
          }

          return <div key={dimension.slug}>{rendered}</div>;
        })}

      {/* Results per page + count display */}
      <div className="flex items-center justify-between gap-4">
        <PageSizeDropdown limit={limit} onLimitChange={onLimitChange} />
        {total !== undefined && (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {isFetching ? (
              <span className="animate-pulse">Loading...</span>
            ) : visibleCount !== undefined ? (
              `Showing ${visibleCount} of ${total}`
            ) : (
              `${total} total`
            )}
          </span>
        )}
      </div>

      {/* Search mode — rendered last */}
      {sortedFilters
        .filter((d) => d.slug === DIMENSION_SLUGS.SEARCH_MODE)
        .map((dimension) => {
          const rendered = renderDimension(dimension);
          if (!rendered) return null;
          return <div key={dimension.slug}>{rendered}</div>;
        })}

      {/* Clear filters button */}
      {hasActiveFilters && (
        <div className="flex justify-end pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
            onClick={onClearFilters}
          >
            <X className="w-3 h-3 mr-1" />
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Advanced filters panel for the search feature.
 * Renders filter dimensions dynamically from the server-provided config.
 * Uses Collapsible for expand/collapse animation.
 */
export function AdvancedFilters({
  isOpen,
  onToggle,
  ...contentProps
}: AdvancedFiltersProps) {
  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors py-1">
        {isOpen ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        Advanced filters
      </CollapsibleTrigger>

      <CollapsibleContent>
        <AdvancedFiltersContent {...contentProps} />
      </CollapsibleContent>
    </Collapsible>
  );
}
